/**
 * SuggestedContactsStrip — iter 508j (Dawn 2026-02-25)
 *
 * A horizontal chip strip that surfaces "possibly save-worthy" unknown
 * phone numbers. Powered by GET /api/contacts/suggested — the backend
 * finds unknown numbers with ≥3 inbound events, extracts a candidate
 * name/role/affiliation via Claude Haiku 4.5, and cross-references
 * against hc_patients for family-relationship auto-linking.
 *
 * Chip language is always hedged ("Possibly …") — the LLM output is
 * a hypothesis, never a claim. The RN must click Save to promote.
 *
 * Actions per chip:
 *   • Save contact       → POST /suggested/{phone}/save
 *   • Not this person    → POST /suggested/{phone}/dismiss
 *   • Not now (7-day)    → POST /suggested/{phone}/snooze
 */
import { useEffect, useState, useCallback } from "react";
import { Sparkles, UserPlus, X, Clock, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useContactSlideover } from "./ContactDetailSlideover";

const API = process.env.REACT_APP_BACKEND_URL;
const authHdr = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function formatPhone(p) {
  const d = String(p || "").replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d.startsWith("1")) return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return p || "";
}

function CONFIDENCE_TONE(c) {
  if (c === "high") return { bg: "#DCFCE7", fg: "#166534", label: "High confidence" };
  if (c === "medium") return { bg: "#FEF9C3", fg: "#854D0E", label: "Medium confidence" };
  return { bg: "#F1F5F9", fg: "#475569", label: "Low confidence" };
}

export default function SuggestedContactsStrip({ onContactSaved }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // phone → true when a mutation is in flight
  const { open } = useContactSlideover();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/contacts/suggested?limit=8`, { headers: authHdr() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSuggestions(Array.isArray(data) ? data : []);
    } catch {
      // Silent — this is a nice-to-have surface, not a critical path.
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const removeFromList = (phone) => {
    setSuggestions(prev => prev.filter(s => s.phone_number !== phone));
  };

  const onSave = async (s) => {
    if (busy[s.phone_number]) return;
    setBusy(b => ({ ...b, [s.phone_number]: true }));
    try {
      const [first, ...rest] = (s.suggested_name || "").split(" ");
      const last = rest.join(" ");
      const roleToType = {
        client: "client", family: "guardian", caregiver: "caregiver",
        provider: "provider", staff: "staff",
      };
      const res = await fetch(
        `${API}/api/contacts/suggested/${encodeURIComponent(s.phone_number)}/save`,
        {
          method: "POST",
          headers: { ...authHdr(), "Content-Type": "application/json" },
          body: JSON.stringify({
            first_name: first || "",
            last_name: last || "",
            contact_type: roleToType[s.suggested_role] || "other",
            tag: s.suggested_affiliation || null,
            link_to_patient_id: s.related_patient_id || null,
          }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json().catch(() => ({}));
      const newId = data?.contact?.id;
      removeFromList(s.phone_number);
      if (onContactSaved) onContactSaved();
      if (newId) {
        // Open the freshly-created contact in the full edit dialog so the
        // user can add/correct the name and other details right away.
        // Mirrors the app-wide "edit a contact" deep-link used by
        // ContactsTab.onEdit and ContactDetailSlideover's Edit action —
        // PhoneBookPanel reads ?edit=<id> on mount and pops the dialog.
        window.location.assign(`/contacts?legacy=1&edit=${encodeURIComponent(newId)}`);
        return;
      }
      toast.success(s.suggested_name ? `Saved ${s.suggested_name}` : "Contact saved");
    } catch (e) {
      toast.error("Save failed — please try again");
    } finally {
      setBusy(b => ({ ...b, [s.phone_number]: false }));
    }
  };

  const onDismiss = async (s) => {
    if (busy[s.phone_number]) return;
    setBusy(b => ({ ...b, [s.phone_number]: true }));
    try {
      await fetch(
        `${API}/api/contacts/suggested/${encodeURIComponent(s.phone_number)}/dismiss`,
        { method: "POST", headers: authHdr() },
      );
      removeFromList(s.phone_number);
    } catch { /* soft-fail */ }
    finally { setBusy(b => ({ ...b, [s.phone_number]: false })); }
  };

  const onSnooze = async (s) => {
    if (busy[s.phone_number]) return;
    setBusy(b => ({ ...b, [s.phone_number]: true }));
    try {
      await fetch(
        `${API}/api/contacts/suggested/${encodeURIComponent(s.phone_number)}/snooze`,
        { method: "POST", headers: authHdr() },
      );
      removeFromList(s.phone_number);
    } catch { /* soft-fail */ }
    finally { setBusy(b => ({ ...b, [s.phone_number]: false })); }
  };

  if (loading) {
    return (
      <div
        className="rounded-lg border p-3 flex items-center gap-2 text-[12px] text-slate-500"
        style={{ background: "#F8FAFC", borderColor: "#E2E8F0" }}
        data-testid="suggested-contacts-loading"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Looking for numbers you might want to save…
      </div>
    );
  }

  if (suggestions.length === 0) return null;

  return (
    <div
      className="rounded-lg border p-3"
      style={{ background: "#FDF6EC", borderColor: "#F5D9A8" }}
      data-testid="suggested-contacts-strip"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles className="w-3.5 h-3.5" style={{ color: "#9A4B00" }} />
        <div className="text-[12px] font-black uppercase tracking-wider" style={{ color: "#9A4B00" }}>
          Suggested contacts
        </div>
        <span className="text-[11px]" style={{ color: "#B36A17" }}>
          · Numbers that keep contacting you but aren&apos;t saved yet
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {suggestions.map(s => {
          const tone = CONFIDENCE_TONE(s.confidence);
          const hasGuess = !!s.suggested_name;
          return (
            <div
              key={s.phone_number}
              className="rounded-lg bg-white border shadow-sm p-2.5 min-w-[280px] max-w-[380px] flex flex-col gap-1.5"
              style={{ borderColor: "#E2E8F0" }}
              data-testid="suggested-contact-chip"
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {hasGuess ? (
                      <>
                        <span className="text-[11px] italic font-medium" style={{ color: "#6B7280" }}>Possibly</span>
                        <span
                          className="text-[13px] font-bold truncate"
                          style={{ color: "#0F172A" }}
                          title={s.suggested_name}
                        >
                          {s.suggested_name}
                        </span>
                      </>
                    ) : (
                      <span className="text-[13px] font-bold" style={{ color: "#0F172A" }}>
                        Unnamed frequent caller
                      </span>
                    )}
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                      style={{ background: tone.bg, color: tone.fg }}
                      title={tone.label}
                    >
                      {s.confidence || "low"}
                    </span>
                  </div>
                  {(s.suggested_role || s.suggested_affiliation) && (
                    <div className="text-[11.5px] mt-0.5" style={{ color: "#475569" }}>
                      {[s.suggested_role, s.suggested_affiliation].filter(Boolean).join(" · ")}
                    </div>
                  )}
                  {s.related_patient_name && (
                    <div className="text-[11.5px] mt-0.5" style={{ color: "#6D28D9" }}>
                      Linked to {s.related_patient_name} ({s.related_relationship || "family"})
                    </div>
                  )}
                  <button
                    className="mt-0.5 text-[11px] font-mono flex items-center gap-1 hover:underline text-left"
                    style={{ color: "#0F172A" }}
                    onClick={() => open({ phone: s.phone_number })}
                    data-testid="suggested-chip-open-timeline"
                    title="Open timeline for this number"
                  >
                    {formatPhone(s.phone_number)}
                    <ExternalLink className="w-2.5 h-2.5 opacity-60" />
                  </button>
                  <div className="text-[10.5px] italic" style={{ color: "#94A3B8" }}>
                    {s.inbound_event_count} inbound in last 30d
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 border-t pt-2" style={{ borderColor: "#F1F5F9" }}>
                <button
                  onClick={() => onSave(s)}
                  disabled={busy[s.phone_number]}
                  className="flex-1 h-7 px-2 rounded-md text-white text-[11px] font-semibold flex items-center justify-center gap-1 hover:brightness-95 disabled:opacity-50"
                  style={{ background: "#10B981" }}
                  title="Save this number and open the contact to add a name & details"
                  data-testid="suggested-chip-save"
                >
                  <UserPlus className="w-3 h-3" />
                  Save contact
                </button>
                <button
                  onClick={() => onDismiss(s)}
                  disabled={busy[s.phone_number]}
                  className="h-7 px-2 rounded-md border text-[11px] font-medium hover:bg-slate-50 disabled:opacity-50"
                  style={{ borderColor: "#E2E8F0", color: "#475569" }}
                  title="Not this person — never suggest again"
                  data-testid="suggested-chip-dismiss"
                >
                  <X className="w-3 h-3" />
                </button>
                <button
                  onClick={() => onSnooze(s)}
                  disabled={busy[s.phone_number]}
                  className="h-7 px-2 rounded-md border text-[11px] font-medium hover:bg-slate-50 disabled:opacity-50"
                  style={{ borderColor: "#E2E8F0", color: "#475569" }}
                  title="Not now — hide for 7 days"
                  data-testid="suggested-chip-snooze"
                >
                  <Clock className="w-3 h-3" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
